import React, { useEffect, useState } from "react";
import * as yup from "yup";
import Swal from "sweetalert2";
import { PageSubHeader } from "../../../../../../provider/PageDataProvider";
import ContentLayout from "../../../../../partials/content/ContentLayout";
import AppForm from "../../../../../partials/content/Forms/AppForm";
import DiscountFormContent from "./DiscountFormContent";
import {
  getDiscount,
  saveDiscount,
} from "../../../../../services/sales/discountService";
import store from "../../../../../store/store";
import { useGlobalData } from "../../../../../../provider/GlobalDataProvider";
import { setObject } from "../../../../../utils/utils";
import { DateTime } from "luxon";
const discountFormSchema = () => {
  return yup.object().shape({
    ID: yup.number(),
    discountCode: yup
      .string()
      .required("Discount Code is required")
      .max(255, "Discount Code maximum characters is 255"),
    discountName: yup
      .string()
      .required("Discount Name is required")
      .max(255, "Discount Name maximum characters is 255"),
    discountType: yup.number().required("Discount Type is required"),
    startDate: yup
      .date()
      .required("Start Date is required")
      .max(yup.ref("endDate"), "Start Date must be less equal to End Date"),
    endDate: yup
      .date()
      .required("End Date is required")
      .min(
        yup.ref("startDate"),
        "End Date must be greater equal to Start Date"
      ),
    minPurchaseType: yup
      .number()
      .required("Minimum Purchase Requirement is required"),
    discountFreeItem: yup.array().when("discountType", {
      is: (value) => value === 2,
      then: yup
        .array()
        .of(
          yup.object().shape({
            itemName: yup.string().required("Item is Required"),
            qty: yup
              .number()
              .transform((value) => (isNaN(value) ? 0 : value))
              .nullable()
              .required("Quantity is required")
              .moreThan(0, "Quantity cannot be zero or empty")
              .default(0),
            uomName: yup.string().required("Unit is Required"),
          })
        )
        .required("Free Items is required"),
    }),
    discountTriggerItem: yup.array().when("minPurchaseType", {
      is: (value) => value === 1,
      then: yup
        .array()
        .of(
          yup.object().shape({
            itemName: yup.string().required("Item is Required"),
            requireQty: yup
              .number()
              .transform((value) => (isNaN(value) ? 0 : value))
              .nullable()
              .required("Quantity is required")
              .moreThan(0, "Quantity cannot be zero or empty")
              .default(0),
            uomName: yup.string().required("Unit is required"),
          })
        )
        .required("Trigger Items is required"),
    }),
  });
};
function DiscountForm({ history, match }) {
  const { socket } = useGlobalData();
  const [discount, setDiscount] = useState({
    discountType: 1,
    minPurchaseType: 1,
    discountCode: "",
    discountName: "",
    discountAmountType: 2,
    discountPercent: 0,
    discountValue: 0,
    minPurchaseAmount: 0,
    startDate: DateTime.now().startOf("day"),
    endDate: DateTime.now()
      .endOf("day")
      .plus({ days: 14 }),
    discountStatus: 0,
    discountStatusDesc: "",
    maxDiscountUse: 0,
    appliedQuot: false,
    appliedSo: false,
    appliedDs: false,
    appliedOutlet: false,
    discountOutlet: [],
    discountTriggerItem: [],
    discountFreeItem: [],
    discountAmountOffItem: [],
  });

  const discountID = match.params.discountID;
  useEffect(() => {
    const { auth } = store.getState();
    if (discountID > 0) {
      setObject(socket, "lock", discountID, "DSPR", auth.user)
        .then((response) => {
          getDiscount(discountID).then((response) => {
            const { data } = response.data;
            setDiscount(data.discount);
          });
        })
        .catch((error) => {
          history.push("/sales/discount/discount-list");
        });
    }
    return () => {
      if (discountID > 0) {
        setObject(socket, "release", discountID, "DSPR", auth.user);
      }
    };
  }, [discountID]);
  return (
    <React.Fragment>
      <PageSubHeader
        title="Discount Form"
        breadcrumbs={[
          { page: "/menu", title: "Menu", active: false },
          {
            page: "/sales/discount/discount-list",
            title: "Discounts",
            active: false,
          },
          {
            page: "/sales/discount/discount-form",
            title: "Discount Form",
            active: true,
          },
        ]}
        actionButtons={[
          {
            title: "",
            color: "label-brand",
            icon: "fas fa-backward",
            onClick: () => {
              history.push("/sales/discount/discount-list");
            },
          },
        ]}
      />
      <AppForm
        headSubmitButton={true}
        initialValues={discount}
        validationSchema={discountFormSchema}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          const discountValues = { ...values };
          discountValues.maxDiscountUse = parseInt(
            discountValues.maxDiscountUse
          );
          discountValues.discountPercent = parseFloat(
            discountValues.discountPercent
          );
          discountValues.discountValue = parseFloat(
            discountValues.discountValue
          );
          for (const triggerItem of discountValues.discountTriggerItem) {
            triggerItem.requireQty = parseFloat(triggerItem.requireQty);
          }
          for (const freeItem of discountValues.discountFreeItem) {
            freeItem.qty = parseFloat(freeItem.qty);
          }
          for (const itemAmountOff of discountValues.discountAmountOffItem) {
            itemAmountOff.discountPercent = parseFloat(
              itemAmountOff.discountPercent
            );
            itemAmountOff.discountValue = parseFloat(
              itemAmountOff.discountValue
            );
          }
          Swal.mixin({
            customClass: {
              confirmButton: "btn btn-success btn-md btn-pill btn-bold",
              cancelButton: "btn btn-danger btn-md btn-pill btn-bold",
            },
            buttonsStyling: false,
          })
            .fire({
              title: "Are you sure want to save this Discount ?",
              text: "Please note that you are responsible for this action",
              icon: "question",
              showCloseButton: true,
              showCancelButton: true,
              confirmButtonText: `<i class="fas fa-check-circle"></i> Yes, save it`,
              cancelButtonText: `<i class="fas fa-times-circle"></i> No, cancel`,
              reverseButtons: true,
            })
            .then((result) => {
              if (result.isConfirmed) {
                saveDiscount(discountValues).then((response) => {
                  Swal.mixin({
                    customClass: {
                      confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                    },
                    buttonsStyling: false,
                  }).fire({
                    title: "Saved",
                    html: response.data.message,
                    icon: "success",
                    showCloseButton: true,
                    confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                  });

                  history.push("/sales/discount/discount-list");
                });
              } else if (result.dismiss === Swal.DismissReason.cancel) {
                Swal.mixin({
                  customClass: {
                    confirmButton: "btn btn-success btn-md btn-pill btn-bold",
                  },
                  buttonsStyling: false,
                }).fire({
                  title: "Cancelled",
                  text: "No change has been made",
                  icon: "info",
                  showCloseButton: true,
                  confirmButtonText: `<i class="fas fa-check-circle"></i> Okay, noted`,
                });
              }
            });
        }}
      >
        <ContentLayout>
          <DiscountFormContent />
        </ContentLayout>
      </AppForm>
    </React.Fragment>
  );
}

export default DiscountForm;
